import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { ref, onValue } from 'firebase/database';

import { DatePicker } from 'components';
import { loginState } from 'global-stores';
import { useFirebaseInit } from 'hooks';
import { PageHead, Pager, SearchBox } from 'views/design-system';
import { ESIGN_PURPOSE } from 'views/pipelines/components';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';
import {
	IInboxAction,
	ISignedDocInbox,
	InboxActions,
	InboxAuth,
	InboxfilteringTagsState,
	InboxLastChangeDate,
	InboxStatus,
	InboxSubject,
	Memory,
	SignDocInboxFilter,
	SignDocInboxSelector,
	useSignedDocsInbox,
} from 'views/signed-doc-inbox';

import { SelectedSentDateRangeState } from './store';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import 'views/signed-doc/signed-doc.scss';
import './esign-sent-deck.scss';

const PAGE_SIZE = 15;

const WORKFLOW_LABEL: Record<string, string> = {
	multiSigner: 'Multi Signer',
	onboarding: 'Onboarding',
	selfSign: 'Self Sign',
};

const inboxTypeOf = (purpose?: string): 'multiSigner' | 'selfSign' | 'onboarding' => {
	if (purpose === 'workflow') return 'multiSigner';
	if (purpose === 'selfsign') return 'selfSign';
	return 'onboarding';
};

const typeOf = (env: ISignedDocInbox) => {
	if (
		env.purpose === ESIGN_PURPOSE.SIGNAGREEMENT ||
		env.purpose === ESIGN_PURPOSE.ACCREDITATION ||
		env.purpose === ESIGN_PURPOSE.MULTISIGNAGREEMENT
	) {
		return 'onboarding';
	}
	return env.origin ?? 'basic';
};

/**
 * The real Sentbox — same data, same permissioning, same action set as
 * production's SignedDocInboxTable, reskinned rather than re-derived. Every
 * cell below renders the actual production component (InboxSubject,
 * InboxStatus, InboxAuth, InboxLastChangeDate, InboxActions) so Sign, Resend,
 * Download, History, Void, Edit-recipient, View and Correct all keep working
 * exactly as they do today — only the surrounding table/page chrome is new.
 */
export const EsignSentDeck = () => {
	const userData = useRecoilValue(loginState) ?? {};
	const { data: envelopes, isLoaded } = useRecoilValue(SignDocInboxSelector);
	const { getSignedDocsInbox } = useSignedDocsInbox();
	const { database, authReady } = useFirebaseInit();
	const { business, email } = userData as { business?: string; email?: string };
	const { checkUserWritePermission } = useUserRoles();
	const filteringTags = useRecoilValue(InboxfilteringTagsState);
	const dateRange = useRecoilValue(SelectedSentDateRangeState);

	const isUserPermissionWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Sent),
		[checkUserWritePermission]
	);

	const [typed, setTyped] = useState('');
	const [page, setPage] = useState(1);

	const reload = useCallback(() => {
		getSignedDocsInbox();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		reload();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* Same RTDB listener production's Sentbox uses to know when to re-poll —
	 * without it this page would only ever show what was loaded on mount. */
	useEffect(() => {
		if (business && authReady) {
			const dataRef = ref(database, business);
			onValue(dataRef, snapshot => {
				const data = snapshot.val();
				if (data.timeStamp === Memory.getTimeStamp() && !data.envelopeId) return;
				Memory.setTimeStamp(data.timeStamp);
				if (data?.businessId === business) getSignedDocsInbox(false);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authReady, business]);

	const completedRecipientsCount = useCallback(
		(inbox: ISignedDocInbox) => {
			let count = 0;
			inbox.recipients.forEach(r => {
				if (r.status === 'completed') count += 1;
			});
			const recipient = inbox.recipients.find(({ email: e }) => e === email);
			return { count, recipient };
		},
		[email]
	);

	/* Exact status derivation from production's inbox-table.tsx getAction — kept
	 * verbatim so the chip/action a row shows can never disagree with what the
	 * real InboxActions component decides to render for it. */
	const getAction = useCallback(
		(inbox: ISignedDocInbox): IInboxAction => {
			const shouldShowDownloadBtn =
				inbox.status === 'completed' ||
				completedRecipientsCount(inbox).count === inbox.recipients.length;
			const allRecipientsCompleted = inbox.recipients.every(r => r.status === 'completed');

			if (inbox.status === 'voided') return 'voided';
			if (inbox.status === 'configured' && allRecipientsCompleted) return 'processing';
			if (shouldShowDownloadBtn) return 'completed';
			if (!inbox.signOrder) {
				if (
					completedRecipientsCount(inbox)?.recipient?.status === 'pending' &&
					inbox.purpose !== ESIGN_PURPOSE.SIGNAGREEMENT &&
					inbox.purpose !== ESIGN_PURPOSE.MULTISIGNAGREEMENT
				) {
					return 'needToSign';
				}
				return 'waitingForOthers';
			}
			const nextUser = inbox.recipients.find(r => r.status !== 'completed');
			if (
				nextUser &&
				nextUser.email.toLowerCase() === email?.toLowerCase() &&
				inbox.purpose !== ESIGN_PURPOSE.SIGNAGREEMENT &&
				inbox.purpose !== ESIGN_PURPOSE.MULTISIGNAGREEMENT
			) {
				return 'needToSign';
			}
			return 'waitingForOthers';
		},
		[completedRecipientsCount, email]
	);

	const decorated = useMemo(
		() =>
			(envelopes ?? []).map((env: ISignedDocInbox) => ({
				env,
				action: getAction(env),
				inboxType: inboxTypeOf(env.purpose as string),
			})),
		[envelopes, getAction]
	);

	const filtered = useMemo(() => {
		const q = typed.trim().toLowerCase();
		const statusTags: string[] = filteringTags?.actionStatus ?? [];
		const typeTags: string[] = filteringTags?.inboxType ?? [];
		const [{ startDate, endDate } = { startDate: null, endDate: null }] = dateRange ?? [];
		return decorated.filter(({ env, action, inboxType }) => {
			if (statusTags.length && !statusTags.includes(action)) return false;
			if (typeTags.length && !typeTags.includes(inboxType)) return false;
			if (startDate && endDate) {
				const created = new Date(env.createdAt).getTime();
				if (created < new Date(startDate).getTime() || created > new Date(endDate).getTime()) {
					return false;
				}
			}
			if (!q) return true;
			const haystack = [env.name, ...(env.recipients ?? []).map(r => `${r.fullName ?? r.name} ${r.email}`)]
				.join(' ')
				.toLowerCase();
			return haystack.includes(q);
		});
	}, [decorated, typed, filteringTags, dateRange]);

	const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	return (
		<div className="sv2-scope sv2 esign-sent-deck">
			<PageHead
				title="Sent"
				sub={
					<>
						{(envelopes ?? []).length} envelopes &middot; {isLoaded ? 'synced' : 'syncing'}{' '}
						&middot; statuses read live from the envelope-list API
					</>
				}
			/>

			<div className="ctl">
				<DatePicker stateType={SelectedSentDateRangeState} />
				<SearchBox
					value={typed}
					onChange={v => {
						setTyped(v);
						setPage(1);
					}}
					placeholder="Search subject or recipient"
					title="Searches the subject and every recipient's name and email"
				/>
				<div className="sentfilter">
					<SignDocInboxFilter />
				</div>
				<Pager page={page} pages={pages} onChange={setPage} />
			</div>

			<div className="panel" style={{ marginTop: 12 }}>
				<div className="tscroll">
					<table className="stbl etbl">
						<thead>
							<tr>
								<th className="subcol">Subject</th>
								<th className="stcol">Status</th>
								<th>WorkFlow</th>
								<th>Type</th>
								<th>Face Auth.</th>
								<th>Created At</th>
								<th className="accol">Actions</th>
							</tr>
						</thead>
						<tbody>
							{shown.map(({ env, action, inboxType }) => (
								<tr key={env._id}>
									<td className="od subcol">
										<InboxSubject inbox={env} />
									</td>
									<td className="fl stcol">
										<InboxStatus inbox={env} action={action} />
									</td>
									<td className="mid">{WORKFLOW_LABEL[inboxType]}</td>
									<td className="mid">
										<div className={`sign-doc-capsule sign-doc-capsule--${typeOf(env)}`}>
											{typeOf(env)}
										</div>
									</td>
									<td className="mid">
										<InboxAuth type={env.authType} />
									</td>
									<td className="mid">
										<InboxLastChangeDate inbox={env} />
									</td>
									<td className="accol">
										<InboxActions
											inbox={env}
											action={action}
											inboxType={inboxType}
											type="sentBox"
											isUserPermissionWrite={isUserPermissionWrite}
										/>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{!isLoaded && <div className="sv2-loading">Loading envelopes…</div>}
				{isLoaded && filtered.length === 0 && (
					<div className="sv2-empty">No envelopes match this filter.</div>
				)}
			</div>
		</div>
	);
};

export default EsignSentDeck;
